import { createSlice } from "@reduxjs/toolkit";
import {
  BorderouHistoryInterface,
  IncompleteInvoicesInterface,
  MonitoringPlasticBoxesInterface,
  OrdersByDeliveryDayInterface,
  PlasticBoxesListInterface,
  ScheduleWorkingPointsDetailsInterface,
  ScheduleWorkingPointsInterface,
  ScheduleWorkingRoutesDetailsInterface,
  ScheduleWorkingRoutesInterface,
  SearchPackageByOpDataInterface,
  WorkingRoutesNodesInterface,
} from "../interfaces/expedition.interface";
import {
  getIncompleteInvoices,
  getOrdersByDeliveryDay,
  getScannedPackageByOp,
  getScheduleWorkingPoints,
  getScheduleWorkingPointsDetails,
  getScheduleWorkingRoutes,
  getScheduleWorkingRoutesDetails,
  getWorkingRoutesNodes,
  postBorderouHistory,
  postMonitoringPlasticBoxes,
} from "../api/expedition.api";

const initialExpeditionState: {
  scheduleWorkingPoints: ScheduleWorkingPointsInterface;
  scheduleWorkingPointsDetails: ScheduleWorkingPointsDetailsInterface;
  scheduleWorkingRoutes: ScheduleWorkingRoutesInterface;
  scheduleWorkingRoutesDetails: ScheduleWorkingRoutesDetailsInterface;
  workingRoutesNodes: WorkingRoutesNodesInterface;
  incompleteInvoices: IncompleteInvoicesInterface;
  plasticBoxesScanned: {
    data: PlasticBoxesListInterface[];
  };
  monitoringPlasticBoxes: MonitoringPlasticBoxesInterface;
  borderouHistory: BorderouHistoryInterface;
  searchPackageByOp: SearchPackageByOpDataInterface;
  ordersByDeliveryDay: OrdersByDeliveryDayInterface;
} = {
  scheduleWorkingPoints: {
    data: {
      nrPagini: 1,
      nrTotal: 1,
      workingPoints: [],
    },
    isLoadingScheduleWorkingPoints: false,
    isErrorScheduleWorkingPoints: false,
    errorMessageScheduleWorkingPoints: "",
  },
  scheduleWorkingPointsDetails: {
    data: [],
    isLoadingScheduleWorkingPointsDetails: false,
    isErrorScheduleWorkingPointsDetails: false,
    errorMessageScheduleWorkingPointsDetails: "",
  },
  scheduleWorkingRoutes: {
    data: {
      nrPagini: 1,
      nrTotal: 1,
      routes: [],
    },
    isLoadingScheduleWorkingRoutes: false,
    isErrorScheduleWorkingRoutes: false,
    errorMessageScheduleWorkingRoutes: "",
  },
  scheduleWorkingRoutesDetails: {
    data: [],
    isLoadingScheduleWorkingRoutesDetails: false,
    isErrorScheduleWorkingRoutesDetails: false,
    errorMessageScheduleWorkingRoutesDetails: "",
  },
  workingRoutesNodes: {
    data: [],
    isLoadingWorkingRoutesNodes: false,
    isErrorWorkingRoutesNodes: false,
    errorMessageWorkingRoutesNodes: "",
  },
  incompleteInvoices: {
    data: [],
    isLoadingIncompleteInvoices: false,
    isErrorIncompleteInvoices: false,
    errorMessageIncompleteInvoices: "",
  },
  plasticBoxesScanned: {
    data: [],
  },
  monitoringPlasticBoxes: {
    data: {
      cutiiPlastic: [],
      nrPagini: 1,
      nrTotal: 1,
    },
    isLoadingMonitoringPlasticBoxes: false,
    isErrorMonitoringPlasticBoxes: false,
    errorMessageMonitoringPlasticBoxes: "",
  },
  borderouHistory: {
    data: [],
    isLoadingBorderouHistory: false,
    isErrorBorderouHistory: false,
    errorMessageBorderouHistory: "",
  },
  searchPackageByOp: {
    data: {
      total: 1,
      current_page: 1,
      data: [],
    },
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
  ordersByDeliveryDay: {
    data: {
      total: 1,
      current_page: 1,
      data: [],
    },
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
};

const expeditionReducer = createSlice({
  name: "expedition",
  initialState: initialExpeditionState,
  reducers: {
    setAddPlasticBox: (
      state,
      action: {
        payload: PlasticBoxesListInterface;
      },
    ) => {
      const plasticBoxesList = state.plasticBoxesScanned.data;
      const newBox = action.payload;

      const alreadyInList = plasticBoxesList.some(
        (box) => box.sn === newBox.sn,
      );

      if (alreadyInList) {
        return state;
      }

      return {
        ...state,
        plasticBoxesScanned: {
          data: [...plasticBoxesList, newBox],
        },
      };
    },
    resetPlasticBoxesScanned: (state) => {
      return {
        ...state,
        plasticBoxesScanned: {
          ...initialExpeditionState.plasticBoxesScanned,
        },
      };
    },
    resetScheduleWorkingPoints: (state) => {
      return {
        ...state,
        scheduleWorkingPoints: {
          ...initialExpeditionState.scheduleWorkingPoints,
        },
      };
    },
    resetScheduleWorkingPointsDetails: (state) => {
      return {
        ...state,
        scheduleWorkingPointsDetails: {
          ...initialExpeditionState.scheduleWorkingPointsDetails,
        },
      };
    },
    resetScheduleWorkingRoutesDetails: (state) => {
      return {
        ...state,
        scheduleWorkingRoutesDetails: {
          ...initialExpeditionState.scheduleWorkingRoutesDetails,
        },
      };
    },
    resetSearchPackageByOp: (state) => {
      return {
        ...state,
        searchPackageByOp: {
          ...initialExpeditionState.searchPackageByOp,
        },
      };
    },
    clearOrderByDeliveryDay: (state) => {
      return {
        ...state,
        ordersByDeliveryDay: {
          ...initialExpeditionState.ordersByDeliveryDay,
        },
      };
    },
  },
  extraReducers: (builder) => {
    // get schedule working points start
    builder.addCase(getScheduleWorkingPoints.pending, (state) => {
      return {
        ...state,
        scheduleWorkingPoints: {
          ...initialExpeditionState.scheduleWorkingPoints,
          isLoadingScheduleWorkingPoints: true,
        },
      };
    });
    builder.addCase(getScheduleWorkingPoints.rejected, (state, action: any) => {
      return {
        ...state,
        scheduleWorkingPoints: {
          ...state.scheduleWorkingPoints,
          isLoadingScheduleWorkingPoints: false,
          errorMessageScheduleWorkingPoints: action.payload.message,
          isErrorScheduleWorkingPoints: true,
        },
      };
    });
    builder.addCase(getScheduleWorkingPoints.fulfilled, (state, action) => {
      return {
        ...state,
        scheduleWorkingPoints: {
          ...state.scheduleWorkingPoints,
          isLoadingScheduleWorkingPoints: false,
          data: action.payload,
        },
      };
    });
    // get schedule working points end

    // get  schedule working routes details start
    builder.addCase(getScheduleWorkingPointsDetails.pending, (state) => {
      return {
        ...state,
        scheduleWorkingPointsDetails: {
          ...initialExpeditionState.scheduleWorkingPointsDetails,
          isLoadingScheduleWorkingPointsDetails: true,
        },
      };
    });
    builder.addCase(
      getScheduleWorkingPointsDetails.rejected,
      (state, action: any) => {
        return {
          ...state,
          scheduleWorkingPointsDetails: {
            ...state.scheduleWorkingPointsDetails,
            isLoadingScheduleWorkingPointsDetails: false,
            errorMessageScheduleWorkingPointsDetails: action.payload.message,
            isErrorScheduleWorkingPointsDetails: true,
          },
        };
      },
    );
    builder.addCase(
      getScheduleWorkingPointsDetails.fulfilled,
      (state, action) => {
        return {
          ...state,
          scheduleWorkingPointsDetails: {
            ...state.scheduleWorkingPointsDetails,
            isLoadingScheduleWorkingPointsDetails: false,
            data: action.payload,
          },
        };
      },
    );
    // end get schedule working points details end
    // get schedule working routes start
    builder.addCase(getScheduleWorkingRoutes.pending, (state) => {
      return {
        ...state,
        scheduleWorkingRoutes: {
          ...initialExpeditionState.scheduleWorkingRoutes,
          isLoadingScheduleWorkingRoutes: true,
        },
      };
    });
    builder.addCase(getScheduleWorkingRoutes.rejected, (state, action: any) => {
      return {
        ...state,
        scheduleWorkingRoutes: {
          ...state.scheduleWorkingRoutes,
          isLoadingScheduleWorkingRoutes: false,
          errorMessageScheduleWorkingRoutes: action.payload.message,
          isErrorScheduleWorkingRoutes: true,
        },
      };
    });

    builder.addCase(getScheduleWorkingRoutes.fulfilled, (state, action) => {
      return {
        ...state,
        scheduleWorkingRoutes: {
          ...state.scheduleWorkingRoutes,
          isLoadingScheduleWorkingRoutes: false,
          data: action.payload,
        },
      };
    });
    // get schedule working routes end
    // get  schedule working routes details start
    builder.addCase(getScheduleWorkingRoutesDetails.pending, (state) => {
      return {
        ...state,
        scheduleWorkingRoutesDetails: {
          ...initialExpeditionState.scheduleWorkingRoutesDetails,
          isLoadingScheduleWorkingRoutesDetails: true,
        },
      };
    });
    builder.addCase(
      getScheduleWorkingRoutesDetails.rejected,
      (state, action: any) => {
        return {
          ...state,
          scheduleWorkingRoutesDetails: {
            ...state.scheduleWorkingRoutesDetails,
            isLoadingScheduleWorkingRoutesDetails: false,
            errorMessageScheduleWorkingRoutesDetails: action.payload.message,
            isErrorScheduleWorkingRoutesDetails: true,
          },
        };
      },
    );
    builder.addCase(
      getScheduleWorkingRoutesDetails.fulfilled,
      (state, action) => {
        return {
          ...state,
          scheduleWorkingRoutesDetails: {
            ...state.scheduleWorkingRoutesDetails,
            isLoadingScheduleWorkingRoutesDetails: false,
            data: action.payload,
          },
        };
      },
    );
    //  get  schedule working routes details end
    // get working routes nodes start
    builder.addCase(getWorkingRoutesNodes.pending, (state) => {
      return {
        ...state,
        workingRoutesNodes: {
          ...initialExpeditionState.workingRoutesNodes,
          isLoadingWorkingRoutesNodes: true,
        },
      };
    });
    builder.addCase(getWorkingRoutesNodes.rejected, (state, action: any) => {
      return {
        ...state,
        workingRoutesNodes: {
          ...state.workingRoutesNodes,
          isLoadingWorkingRoutesNodes: false,
          errorMessageWorkingRoutesNodes: action.payload.message,
          isErrorWorkingRoutesNodes: true,
        },
      };
    });
    builder.addCase(getWorkingRoutesNodes.fulfilled, (state, action) => {
      return {
        ...state,
        workingRoutesNodes: {
          ...state.workingRoutesNodes,
          isLoadingWorkingRoutesNodes: false,
          data: action.payload,
        },
      };
    });
    // get working routes nodes end

    // incomplete invoices start
    builder.addCase(getIncompleteInvoices.pending, (state) => {
      return {
        ...state,
        incompleteInvoices: {
          ...initialExpeditionState.incompleteInvoices,
          isLoadingIncompleteInvoices: true,
        },
      };
    });
    builder.addCase(getIncompleteInvoices.rejected, (state, action: any) => {
      return {
        ...state,
        incompleteInvoices: {
          ...state.incompleteInvoices,
          isLoadingIncompleteInvoices: false,
          errorMessageIncompleteInvoices: action.payload.message,
          isErrorIncompleteInvoices: true,
        },
      };
    });
    builder.addCase(getIncompleteInvoices.fulfilled, (state, action) => {
      return {
        ...state,
        incompleteInvoices: {
          ...state.incompleteInvoices,
          isLoadingIncompleteInvoices: false,
          data: action.payload,
        },
      };
    });
    // incomplete invoices end

    // post monitoring plastic boxes start
    builder.addCase(postMonitoringPlasticBoxes.pending, (state) => {
      return {
        ...state,
        monitoringPlasticBoxes: {
          ...initialExpeditionState.monitoringPlasticBoxes,
          isLoadingMonitoringPlasticBoxes: true,
        },
      };
    });
    builder.addCase(
      postMonitoringPlasticBoxes.rejected,
      (state, action: any) => {
        return {
          ...state,
          monitoringPlasticBoxes: {
            ...state.monitoringPlasticBoxes,
            isLoadingMonitoringPlasticBoxes: false,
            errorMessageMonitoringPlasticBoxes: action.payload.message,
            isErrorMonitoringPlasticBoxes: true,
          },
        };
      },
    );
    builder.addCase(postMonitoringPlasticBoxes.fulfilled, (state, action) => {
      return {
        ...state,
        monitoringPlasticBoxes: {
          ...state.monitoringPlasticBoxes,
          isLoadingMonitoringPlasticBoxes: false,
          data: action.payload,
        },
      };
    });
    // post monitoring plastic boxes end

    // borderou history start
    builder.addCase(postBorderouHistory.pending, (state) => {
      return {
        ...state,
        borderouHistory: {
          ...initialExpeditionState.borderouHistory,
          isLoadingBorderouHistory: true,
        },
      };
    });
    builder.addCase(postBorderouHistory.rejected, (state, action: any) => {
      return {
        ...state,
        borderouHistory: {
          ...state.borderouHistory,
          isLoadingBorderouHistory: false,
          errorMessageBorderouHistory: action.payload.message,
          isErrorBorderouHistory: true,
        },
      };
    });
    builder.addCase(postBorderouHistory.fulfilled, (state, action) => {
      return {
        ...state,
        borderouHistory: {
          ...state.borderouHistory,
          isLoadingBorderouHistory: false,
          data: action.payload,
        },
      };
    });
    // borderou history end

    // search package by op start
    builder.addCase(getScannedPackageByOp.pending, (state) => {
      return {
        ...state,
        searchPackageByOp: {
          ...initialExpeditionState.searchPackageByOp,
          isLoading: true,
        },
      };
    });
    builder.addCase(getScannedPackageByOp.rejected, (state, action: any) => {
      return {
        ...state,
        searchPackageByOp: {
          ...state.searchPackageByOp,
          isLoading: false,
          errorMessage: action.payload.message,
          isError: true,
        },
      };
    });
    builder.addCase(getScannedPackageByOp.fulfilled, (state, action) => {
      return {
        ...state,
        searchPackageByOp: {
          ...state.searchPackageByOp,
          isLoading: false,
          data: action.payload,
        },
      };
    });
    // search package by op end
    // get orders by delivery day start
    builder.addCase(getOrdersByDeliveryDay.pending, (state) => {
      return {
        ...state,
        ordersByDeliveryDay: {
          ...initialExpeditionState.ordersByDeliveryDay,
          isLoading: true,
        },
      };
    });
    builder.addCase(getOrdersByDeliveryDay.rejected, (state, action: any) => {
      return {
        ...state,
        ordersByDeliveryDay: {
          ...state.ordersByDeliveryDay,
          isLoading: false,
          errorMessage: action.payload.message,
          isError: true,
        },
      };
    });
    builder.addCase(getOrdersByDeliveryDay.fulfilled, (state, action: any) => {
      return {
        ...state,
        ordersByDeliveryDay: {
          ...state.ordersByDeliveryDay,
          isLoading: false,
          data: action.payload,
        },
      };
    });
    // get orders by delivery day end
  },
});

export const {
  resetScheduleWorkingPoints,
  resetScheduleWorkingPointsDetails,
  resetScheduleWorkingRoutesDetails,
  setAddPlasticBox,
  resetPlasticBoxesScanned,
  resetSearchPackageByOp,
  clearOrderByDeliveryDay,
} = expeditionReducer.actions;

export default expeditionReducer.reducer;
